import { getToken, onMessage } from 'firebase/messaging';
import { messaging, VAPID_KEY } from '../firebase/config';

export const notificationService = {
  userId: null,
  fcmToken: null,
  serviceWorkerRegistration: null,

  // Initialize or get user ID
  getUserId() {
    if (!this.userId) {
      this.userId = localStorage.getItem('cfb_user_id');
      if (!this.userId) {
        this.userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('cfb_user_id', this.userId);
      }
    }
    return this.userId;
  },

  // Request notification permission
  async requestPermission() {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return false;
    }

    try {
      console.log('Requesting notification permission...');
      const permission = await Notification.requestPermission();
      console.log('Notification permission result:', permission);

      // Get Vue PWA service worker registration after permission is granted
      if (permission === 'granted' && 'serviceWorker' in navigator) {
        try {
          // Vue PWA service worker is automatically registered
          let registration = await navigator.serviceWorker.getRegistration();

          if (!registration) {
            console.log('Waiting for Vue PWA service worker registration...');
            // Wait for Vue to register its service worker
            await navigator.serviceWorker.ready;
            registration = await navigator.serviceWorker.getRegistration();
          }

          if (registration) {
            console.log('Using Vue PWA service worker:', registration.scope);
            console.log('Service worker state:', registration.active?.state || 'activating');

            // Store registration for later use
            this.serviceWorkerRegistration = registration;
          } else {
            console.warn('No service worker registration found');
          }
        } catch (error) {
          console.error('Service worker access failed:', error);
        }
      }

      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  },

  // Check if notifications are supported and permission granted
  isSupported() {
    return 'Notification' in window && 'serviceWorker' in navigator;
  },

  hasPermission() {
    return Notification.permission === 'granted';
  },

  // Subscribe to Firebase Cloud Messaging
  async subscribeToPushNotifications() {
    console.log('🔥 Starting FCM subscription process...');

    if (!this.isSupported() || !this.hasPermission()) {
      console.log('❌ Notifications not supported or permission not granted');
      return { success: false, error: 'Notifications not supported or permission not granted' };
    }

    try {
      console.log('✅ Notifications supported and permission granted');

      // Get Vue PWA service worker registration
      let registration = await navigator.serviceWorker.getRegistration();
      console.log('🔍 Checking for service worker registration:', !!registration);

      if (!registration) {
        console.log('⏳ No service worker registration found, waiting for Vue PWA SW...');
        await navigator.serviceWorker.ready;
        registration = await navigator.serviceWorker.getRegistration();
        console.log('🔍 Service worker ready, registration:', !!registration);
      }

      if (!registration) {
        throw new Error('Failed to get Vue PWA service worker registration');
      }

      console.log('✅ Using Vue PWA service worker registration:', registration.scope);
      console.log('🔧 Service worker state:', registration.active?.state || 'activating');
      this.serviceWorkerRegistration = registration;

      // Check Firebase messaging initialization
      console.log('🔥 Firebase messaging object:', !!messaging);
      console.log('🔑 VAPID key present:', !!VAPID_KEY);

      // Get FCM token with service worker registration
      console.log('⏳ Getting FCM token...');
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration
      });

      if (!token) {
        console.log('❌ FCM token is null or empty');
        throw new Error('Failed to get FCM token - token is null');
      }

      console.log('✅ FCM Token obtained:', token.substring(0, 20) + '...');
      console.log('🔢 Token length:', token.length);
      this.fcmToken = token;

      // Send token to backend
      console.log('📡 Sending token to backend...');
      const response = await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          endpoint: token,
          p256dh_key: 'firebase-p256dh-key', // FCM handles this internally
          auth_key: 'firebase-auth-key', // FCM handles this internally
          user_id: this.getUserId(),
          device_type: 'web'
        })
      });

      console.log('📡 Backend response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.log('❌ Backend registration failed:', response.status, errorText);
        throw new Error(`Backend registration failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Backend registration successful:', result);

      return {
        success: true,
        token: token,
        subscription_id: result.subscription_id
      };
    } catch (error) {
      console.error('❌ Failed to subscribe to FCM:', error);
      console.error('❌ Error stack:', error.stack);
      return { success: false, error: error.message };
    }
  },

  // Send a test notification via backend
  async sendTestNotification(title = 'Test Notification', body = 'This is a test notification from CFB Scores Vibe!') {
    try {
      // Get the current FCM token from the subscription
      let endpoint = this.fcmToken;

      if (!endpoint) {
        // Try to get a fresh token
        console.log('No stored FCM token, attempting to get fresh token...');
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          const token = await getToken(messaging, {
            vapidKey: VAPID_KEY,
            serviceWorkerRegistration: registration
          });
          endpoint = token;
        }
      }

      if (!endpoint) {
        throw new Error('No FCM token available. Please enable notifications first.');
      }

      console.log('Sending test notification with token:', endpoint.substring(0, 20) + '...');

      const response = await fetch('/api/push/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          endpoint: endpoint,
          title: title,
          body: body
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to send test notification: ${response.status}`);
      }

      const result = await response.json();
      console.log('Test notification sent:', result);
      return result;
    } catch (error) {
      console.error('Error sending test notification:', error);
      throw error;
    }
  },

  // Get current FCM token
  getFCMToken() {
    return this.fcmToken;
  },

  // Handle foreground messages
  onForegroundMessage(callback) {
    if (!messaging) {
      console.error('Firebase messaging not available');
      return null;
    }
    return onMessage(messaging, (payload) => {
      console.log('Foreground message received:', payload);
      callback(payload);
    });
  },

  // Helper method to convert VAPID key (for backwards compatibility)
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
};